import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Calculator, TrendingUp, Info, Download, Share2, BookOpen } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const CoutDirigeantSimulator = () => {
  const [inputs, setInputs] = useState({
    salaireBrutMensuel: 3500,
    statut: 'sasu',
    avantagesNature: '',
    ticketsRestaurant: ''
  });

  const [results, setResults] = useState({});

  const calculate = () => {
    const salaireBrut = inputs.salaireBrutMensuel;
    const avantages = parseFloat(inputs.avantagesNature) || 0;
    const tickets = parseFloat(inputs.ticketsRestaurant) || 0;
    const salaireBrutAnnuel = salaireBrut * 12;

    let chargesPatronales, chargesSalariales;
    if (inputs.statut === 'sasu') {
      chargesPatronales = salaireBrutAnnuel * 0.42;
      chargesSalariales = salaireBrutAnnuel * 0.22;
    } else {
      chargesPatronales = salaireBrutAnnuel * 0.30;
      chargesSalariales = salaireBrutAnnuel * 0.15;
    }

    const salaireNet = salaireBrutAnnuel - chargesSalariales;
    const coutEntreprise = salaireBrutAnnuel + chargesPatronales + avantages + tickets;
    const coutMensuelEntreprise = coutEntreprise / 12;
    const ratioNetCout = coutEntreprise > 0 ? (salaireNet / coutEntreprise) * 100 : 0;
    const chargesTotal = chargesPatronales + chargesSalariales;
    const tauxChargesGlobal = salaireBrutAnnuel > 0 ? (chargesTotal / salaireBrutAnnuel) * 100 : 0;

    setResults({
      salaireBrutAnnuel,
      chargesPatronales: Math.round(chargesPatronales),
      chargesSalariales: Math.round(chargesSalariales),
      salaireNet: Math.round(salaireNet),
      salaireNetMensuel: Math.round(salaireNet / 12),
      coutEntreprise: Math.round(coutEntreprise),
      coutMensuelEntreprise: Math.round(coutMensuelEntreprise),
      ratioNetCout: ratioNetCout.toFixed(1),
      chargesTotal: Math.round(chargesTotal),
      tauxChargesGlobal: tauxChargesGlobal.toFixed(1)
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: 'Simulateur Coût Dirigeant - Bozzam', url });
    } else {
      navigator.clipboard.writeText(url);
      alert('Lien copié !');
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text('Bozzam - Coût Total Dirigeant', 15, 25);
    doc.setFontSize(10);
    doc.text('Simulation réalisée sur blog.bozzam.com', 15, 35);

    doc.setTextColor(0, 0, 0);
    let y = 55;
    doc.setFontSize(14);
    doc.text('Paramètres', 15, y);
    y += 10;
    doc.setFontSize(11);
    doc.text(`Statut : ${inputs.statut === 'sasu' ? 'SASU (assimilé salarié)' : 'EURL (TNS)'}`, 15, y);
    y += 7;
    doc.text(`Salaire brut mensuel : ${inputs.salaireBrutMensuel.toLocaleString()} EUR`, 15, y);
    y += 15;

    doc.setFontSize(14);
    doc.text('Résultats', 15, y);
    y += 10;
    doc.setFontSize(11);
    const lines = [
      [`Salaire brut annuel`, `${results.salaireBrutAnnuel?.toLocaleString()} EUR`],
      [`Charges patronales`, `${results.chargesPatronales?.toLocaleString()} EUR`],
      [`Charges salariales`, `${results.chargesSalariales?.toLocaleString()} EUR`],
      [`Salaire net annuel`, `${results.salaireNet?.toLocaleString()} EUR`],
      [`Salaire net mensuel`, `${results.salaireNetMensuel?.toLocaleString()} EUR`],
      [`Coût total entreprise`, `${results.coutEntreprise?.toLocaleString()} EUR`],
      [`Coût mensuel entreprise`, `${results.coutMensuelEntreprise?.toLocaleString()} EUR`],
      [`Ratio net / coût`, `${results.ratioNetCout}%`],
    ];
    lines.forEach(([label, val]) => {
      doc.text(`${label} : ${val}`, 15, y);
      y += 7;
    });

    y += 10;
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('Sources : URSSAF 2026, Code de la Sécurité Sociale. Simulation indicative.', 15, y);
    doc.text('Consultez un expert-comptable pour votre situation personnelle.', 15, y + 5);
    doc.save('simulation-cout-dirigeant-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              Paramètres
            </CardTitle>
            <CardDescription>Du salaire brut au coût total pour l'entreprise</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>Statut du dirigeant</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button
                  data-testid="statut-sasu-btn"
                  variant={inputs.statut === 'sasu' ? 'default' : 'outline'}
                  className={inputs.statut === 'sasu' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('statut', 'sasu')}
                >
                  SASU (assimilé salarié)
                </Button>
                <Button
                  data-testid="statut-eurl-btn"
                  variant={inputs.statut === 'eurl' ? 'default' : 'outline'}
                  className={inputs.statut === 'eurl' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('statut', 'eurl')}
                >
                  EURL (TNS)
                </Button>
              </div>
            </div>

            <div>
              <Label>Salaire brut mensuel : {inputs.salaireBrutMensuel.toLocaleString()}€</Label>
              <Slider
                data-testid="salaire-slider"
                value={[inputs.salaireBrutMensuel]}
                onValueChange={(v) => handleInputChange('salaireBrutMensuel', v[0])}
                max={10000} min={500} step={100}
                className="mt-3 [&_[role=slider]]:bg-blue-600"
              />
            </div>

            <div>
              <Label>Avantages en nature annuels (€)</Label>
              <Input
                data-testid="avantages-input"
                type="text" inputMode="numeric"
                value={inputs.avantagesNature}
                onChange={(e) => handleInputChange('avantagesNature', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="0" className="mt-1"
              />
            </div>

            <div>
              <Label>Tickets restaurant annuels part employeur (€)</Label>
              <Input
                data-testid="tickets-input"
                type="text" inputMode="numeric"
                value={inputs.ticketsRestaurant}
                onChange={(e) => handleInputChange('ticketsRestaurant', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="0" className="mt-1"
              />
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>URSSAF - Taux de cotisations patronales assimilé salarié : ~42%</li>
                    <li>URSSAF - Taux cotisations TNS (EURL) : ~30% patronales</li>
                    <li>Code de la Sécurité Sociale, art. L241-2 et suivants</li>
                    <li>PASS 2026 : 46 368€ (Plafond Annuel Sécurité Sociale)</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Résultats
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Badge className="bg-blue-100 text-blue-800">
                {inputs.statut === 'sasu' ? 'SASU' : 'EURL'}
              </Badge>
              <Badge className="bg-green-100 text-green-800">
                Taux charges : {results.tauxChargesGlobal}%
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs font-medium text-blue-600 mb-1">Salaire brut annuel</p>
                <p className="text-lg font-bold text-blue-900" data-testid="result-brut-annuel">{results.salaireBrutAnnuel?.toLocaleString()}€</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs font-medium text-red-600 mb-1">Charges patronales</p>
                <p className="text-lg font-bold text-red-900">-{results.chargesPatronales?.toLocaleString()}€</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs font-medium text-red-600 mb-1">Charges salariales</p>
                <p className="text-lg font-bold text-red-900">-{results.chargesSalariales?.toLocaleString()}€</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg border-2 border-green-200">
                <p className="text-xs font-medium text-green-600 mb-1">Salaire net annuel</p>
                <p className="text-lg font-bold text-green-900" data-testid="result-net-annuel">{results.salaireNet?.toLocaleString()}€</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200">
              <div className="text-center">
                <h4 className="font-bold text-gray-900 mb-2">Coût total pour l'entreprise</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Annuel</p>
                    <p className="text-3xl font-black text-blue-900" data-testid="result-cout-annuel">{results.coutEntreprise?.toLocaleString()}€</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Mensuel</p>
                    <p className="text-3xl font-black text-blue-900">{results.coutMensuelEntreprise?.toLocaleString()}€</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-amber-600" />
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">Ratio net/coût : {results.ratioNetCout}%</span> — Pour chaque 100€ dépensés par l'entreprise, le dirigeant perçoit {results.ratioNetCout}€ net.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn">
                <Share2 className="h-4 w-4 mr-2" /> Partager
              </Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn">
                <Download className="h-4 w-4 mr-2" /> PDF
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>
                Bozzam App
              </Button>
            </div>

            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : URSSAF 2026 - Code de la Sécurité Sociale</p>
              <p>urssaf.fr | legifrance.gouv.fr | Barèmes officiels 2026</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CoutDirigeantSimulator;
